/* ============================================
   Admin Hosting — Manage hosting orders
   Auto-provisioning via WHM, suspend/reactivate,
   server stats, and DNS status tracking
   ============================================ */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { formatDate, proveedoresDePaquete } from '@/lib/utils'
import type { HostingPedido, WHMServerStats, WHMSyncPreview } from '@/lib/types'
import { PageHeader } from '@/components/ui'
import { IconPlus, IconRefresh, IconServer } from '@/components/icons/Icons'

const estadoBadge: Record<string, string> = {
  pendiente: 'badge-warning',
  pagado: 'badge-info',
  activo: 'badge-success',
  suspendido: 'badge-danger',
  cancelado: 'badge-danger',
}

// Etiqueta + color del proveedor del servicio. verpex_whm es el default de los pedidos existentes.
const proveedorInfo: Record<string, { label: string; badge: string }> = {
  verpex_whm: { label: 'Verpex', badge: 'badge-default' },
  hetzner_docker: { label: 'Hetzner', badge: 'badge-info' },
  cloudflare_edge: { label: 'Cloudflare', badge: 'badge-info' },
  supabase_project: { label: 'Supabase', badge: 'badge-info' },
}
const proveedorFiltros = [
  { key: 'todos', label: 'Todos' },
  { key: 'verpex_whm', label: 'Verpex' },
  { key: 'hetzner_docker', label: 'Hetzner' },
  { key: 'supabase_project', label: 'Supabase' },
  { key: 'cloudflare_edge', label: 'Cloudflare' },
]

export default function HostingPedidos() {
  const api = useApi()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Las cuentas de sistema (reseller raíz, host de infra) se ocultan por defecto; este
  // toggle las trae en modo solo-lectura para poder ver su estado sin poder tocarlas.
  const [verSistema, setVerSistema] = useState(false)
  const { data: pedidos = [], isLoading: loading } = useQuery<HostingPedido[]>({
    queryKey: ['hosting-pedidos', verSistema],
    queryFn: () => api.get<HostingPedido[]>(`/api/hosting/pedidos${verSistema ? '?incluir_sistema=true' : ''}`),
    staleTime: 5 * 60 * 1000,
  })

  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<string>('todos')
  const [selectedPedido, setSelectedPedido] = useState<HostingPedido | null>(null)
  const [showProvisionModal, setShowProvisionModal] = useState(false)
  const [showServerStats, setShowServerStats] = useState(false)
  const [provisionMode, setProvisionMode] = useState<'auto' | 'manual'>('auto')
  const [provisionForm, setProvisionForm] = useState({
    cpanel_url: '',
    cpanel_usuario: '',
    cpanel_password: '',
    dominio: '',
    ip_servidor: '',
  })
  const [provisionLoading, setProvisionLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  const [reembolsoPedido, setReembolsoPedido] = useState<HostingPedido | null>(null)
  const [reembolsoMonto, setReembolsoMonto] = useState('')
  const [reembolsoLoading, setReembolsoLoading] = useState(false)

  function openReembolso(p: HostingPedido) {
    // Prellena con lo cobrado por el dominio (base del reembolso típico); el admin confirma/edita.
    setReembolsoMonto(p.dominio_cobro != null ? String(p.dominio_cobro) : '')
    setReembolsoPedido(p)
  }

  async function doReembolso() {
    if (!reembolsoPedido) return
    const amount = Number(reembolsoMonto)
    if (!Number.isFinite(amount) || amount <= 0) { showToast('Ingresa un monto válido', 'error'); return }
    if (amount > reembolsoPedido.total) { showToast(`El monto no puede superar el total pagado (${reembolsoPedido.total})`, 'error'); return }
    setReembolsoLoading(true)
    try {
      await api.post(`/api/hosting/pedidos/${reembolsoPedido.id}/reembolso`, { amount })
      showToast('Reembolso procesado en MercadoPago', 'success')
      setReembolsoPedido(null)
    } catch (err: any) {
      showToast(err.message || 'Error al procesar el reembolso', 'error')
    } finally {
      setReembolsoLoading(false)
    }
  }
  const [comprobantePreview, setComprobantePreview] = useState<string | null>(null)
  // ─── Servicios gestionados (multi-proveedor) ───
  // El alta de un paquete vive en su propia vista (/hosting/nuevo), no en un modal.
  const [filtroProveedor, setFiltroProveedor] = useState<string>('todos')
  // Checklist de pasos manuales (ej. pausar proyecto Supabase) devuelto por suspender/reactivar.
  const [checklistModal, setChecklistModal] = useState<{ titulo: string; items: string[] } | null>(null)
  // Edición de fechas (vencimiento / corte) en el drawer.
  const [fechasForm, setFechasForm] = useState({ fecha_vencimiento: '', fecha_corte: '' })
  const [savingFechas, setSavingFechas] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncPreview, setSyncPreview] = useState<WHMSyncPreview | null>(null)
  const [syncResult, setSyncResult] = useState<string | null>(null)
  const [confirmModal, setConfirmModal] = useState<{
    title: string; message: string; onConfirm: () => void; variant: 'danger' | 'warning' | 'primary'
  } | null>(null)

  const { data: serverStats, isLoading: statsLoading } = useQuery<WHMServerStats>({
    queryKey: ['whm-server-stats'],
    queryFn: () => api.get<WHMServerStats>('/api/hosting/admin/server-stats'),
    staleTime: 10 * 60 * 1000,
    enabled: showServerStats,
  })

  const estadoTabs = [
    { key: 'todos', label: 'Todos' },
    { key: 'pendiente', label: 'Pendientes' },
    { key: 'pagado', label: 'Pagados' },
    { key: 'activo', label: 'Activos' },
    { key: 'suspendido', label: 'Suspendidos' },
    { key: 'cancelado', label: 'Cancelados' },
  ]

  const filtered = pedidos.filter((p) => {
    const matchEstado = filtroEstado === 'todos' || p.estado === filtroEstado
    const matchProveedor = filtroProveedor === 'todos' || proveedoresDePaquete(p).includes(filtroProveedor)
    const matchSearch =
      !search ||
      p.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase()) ||
      p.codigo?.toLowerCase().includes(search.toLowerCase())
    return matchEstado && matchProveedor && matchSearch
  })

  // Edición de datos de contacto en el drawer (nombre/email/whatsapp). Se sincroniza
  // cada vez que se abre/cambia el pedido seleccionado.
  const [contactForm, setContactForm] = useState({ nombre: '', email: '', whatsapp: '' })
  const [savingContact, setSavingContact] = useState(false)
  useEffect(() => {
    if (selectedPedido) {
      setContactForm({
        nombre: selectedPedido.nombre || '',
        email: selectedPedido.email || '',
        whatsapp: selectedPedido.whatsapp || '',
      })
      setFechasForm({
        // Normaliza a YYYY-MM-DD (algunos registros viejos guardan timestamp completo).
        fecha_vencimiento: (selectedPedido.fecha_vencimiento || '').split('T')[0],
        fecha_corte: (selectedPedido.fecha_corte || '').split('T')[0],
      })
    }
  }, [selectedPedido])

  const contactoCambio = !!selectedPedido && (
    contactForm.nombre.trim() !== (selectedPedido.nombre || '') ||
    contactForm.email.trim() !== (selectedPedido.email || '') ||
    contactForm.whatsapp.trim() !== (selectedPedido.whatsapp || '')
  )

  const handleGuardarContacto = async () => {
    if (!selectedPedido) return
    const email = contactForm.email.trim()
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('Ingresa un email válido', 'error')
      return
    }
    setSavingContact(true)
    try {
      const updated = await api.put<HostingPedido>(`/api/hosting/pedidos/${selectedPedido.id}`, {
        nombre: contactForm.nombre.trim(),
        email,
        whatsapp: contactForm.whatsapp.trim(),
      })
      showToast('Datos de contacto actualizados', 'success')
      queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
      setSelectedPedido(updated)
    } catch (err: any) {
      showToast(err.message || 'Error al guardar', 'error')
    } finally {
      setSavingContact(false)
    }
  }

  const fechasCambio = !!selectedPedido && (
    fechasForm.fecha_vencimiento !== (selectedPedido.fecha_vencimiento || '').split('T')[0] ||
    fechasForm.fecha_corte !== (selectedPedido.fecha_corte || '').split('T')[0]
  )

  const handleGuardarFechas = async () => {
    if (!selectedPedido) return
    setSavingFechas(true)
    try {
      const updated = await api.put<HostingPedido>(`/api/hosting/pedidos/${selectedPedido.id}`, {
        fecha_vencimiento: fechasForm.fecha_vencimiento || null,
        fecha_corte: fechasForm.fecha_corte || null,
      })
      showToast('Fechas actualizadas', 'success')
      queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
      setSelectedPedido(updated)
    } catch (err: any) {
      showToast(err.message || 'Error al guardar las fechas', 'error')
    } finally {
      setSavingFechas(false)
    }
  }

  const handleProvisionar = async () => {
    if (!selectedPedido) return
    setProvisionLoading(true)
    try {
      const payload = provisionMode === 'auto'
        ? { dominio: provisionForm.dominio, modo: 'auto' }
        : { ...provisionForm, modo: 'manual' }
      await api.post(`/api/hosting/pedidos/${selectedPedido.id}/provisionar`, payload)
      showToast('Cuenta provisionada exitosamente', 'success')
      setShowProvisionModal(false)
      setSelectedPedido(null)
      setProvisionForm({ cpanel_url: '', cpanel_usuario: '', cpanel_password: '', dominio: '', ip_servidor: '' })
      queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
    } catch (err: any) {
      showToast(err.message || 'Error al provisionar', 'error')
    } finally {
      setProvisionLoading(false)
    }
  }

  const handleConfirmarPago = async (pedido: HostingPedido) => {
    setConfirmModal({
      title: 'Confirmar pago',
      message: `¿Confirmar pago de ${pedido.nombre} (${pedido.moneda === 'PEN' ? 'S/' : '$'}${pedido.total})?`,
      variant: 'primary',
      onConfirm: async () => {
        setConfirmModal(null)
        try {
          await api.put(`/api/hosting/pedidos/${pedido.id}`, { estado: 'pagado' })
          showToast('Pago confirmado', 'success')
          queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
        } catch (err: any) {
          showToast(err.message || 'Error', 'error')
        }
      },
    })
  }

  const handleCancelar = async (pedido: HostingPedido) => {
    setConfirmModal({
      title: 'Cancelar pedido',
      message: `¿Rechazar/cancelar el pedido ${pedido.codigo} de ${pedido.nombre}? Esta acción no se puede deshacer.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null)
        setActionLoading(pedido.id)
        try {
          await api.put(`/api/hosting/pedidos/${pedido.id}`, { estado: 'cancelado' })
          showToast('Pedido cancelado', 'success')
          queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
        } catch (err: any) {
          showToast(err.message || 'Error al cancelar', 'error')
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  const handleEliminar = async (pedido: HostingPedido) => {
    setConfirmModal({
      title: 'Eliminar pedido',
      message: `¿Eliminar permanentemente ${pedido.codigo}? Esto borrará el pedido de la base de datos.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null)
        setActionLoading(pedido.id)
        try {
          await api.del(`/api/hosting/pedidos/${pedido.id}`)
          showToast('Pedido eliminado', 'success')
          queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
        } catch (err: any) {
          showToast(err.message || 'Error al eliminar', 'error')
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  const handleSuspender = async (pedido: HostingPedido) => {
    const esVerpex = (pedido.proveedor || 'verpex_whm') === 'verpex_whm'
    setConfirmModal({
      title: 'Suspender servicio',
      message: esVerpex
        ? `¿Suspender la cuenta de ${pedido.nombre} (${pedido.dominio || '—'})? El sitio web dejará de estar disponible.\n\nAdemás se generará una copia de seguridad y se le enviará el link de descarga al cliente por email.`
        : `¿Suspender el servicio de ${pedido.nombre} (${pedido.dominio || '—'})? Se bloqueará el acceso en el borde de Cloudflare y el cliente verá la página de "servicio suspendido".`,
      variant: 'warning',
      onConfirm: async () => {
        setConfirmModal(null)
        setActionLoading(pedido.id)
        try {
          const res = await api.post<any>(`/api/hosting/pedidos/${pedido.id}/suspender`, {})
          showToast('Servicio suspendido.', 'success')
          if (res?.checklist?.length) setChecklistModal({ titulo: 'Pasos manuales para completar la suspensión', items: res.checklist })
          queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
        } catch (err: any) {
          showToast(err.message || 'Error al suspender', 'error')
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  const handleReactivar = async (pedido: HostingPedido) => {
    setActionLoading(pedido.id)
    try {
      const res = await api.post<any>(`/api/hosting/pedidos/${pedido.id}/reactivar`, {})
      showToast('Servicio reactivado', 'success')
      if (res?.checklist?.length) setChecklistModal({ titulo: 'Pasos manuales para completar la reactivación', items: res.checklist })
      queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
    } catch (err: any) {
      showToast(err.message || 'Error al reactivar', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  const handleTerminar = (pedido: HostingPedido) => {
    setConfirmModal({
      title: 'Dar de baja la cuenta',
      message: `¿Dar de baja la cuenta de ${pedido.nombre} (${pedido.dominio})?\n\nPrimero se generará una copia de seguridad completa y se le enviará el link de descarga al cliente. La cuenta cPanel (archivos, bases de datos y correos) se eliminará automáticamente del servidor SOLO cuando el backup esté guardado en la nube. Esto puede tardar unos minutos.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null)
        setActionLoading(pedido.id)
        try {
          await api.post(`/api/hosting/pedidos/${pedido.id}/eliminar`, {})
          showToast('Baja iniciada: generando backup. La cuenta se eliminará automáticamente al completar el backup.', 'success')
          queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
        } catch (err: any) {
          showToast(err.message || 'Error al eliminar', 'error')
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  const handleBackup = (pedido: HostingPedido) => {
    setConfirmModal({
      title: 'Generar copia de seguridad (interna)',
      message: `¿Generar una copia de seguridad completa de ${pedido.dominio || pedido.plan_nombre}?\n\nIncluye archivos, bases de datos, correos y configuraciones. Es una copia INTERNA para soporte: NO se le envía ningún email al cliente. Cuando esté lista, descargala desde el botón "Descargar ✓".`,
      variant: 'primary',
      onConfirm: async () => {
        setConfirmModal(null)
        setActionLoading(pedido.id)
        try {
          await api.post<{ backup_token: string; status: string }>(`/api/hosting/pedidos/${pedido.id}/backup`, { interno: true })
          showToast('Backup interno iniciado. Podrás descargarlo cuando esté listo (sin avisar al cliente).', 'success')
          queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
        } catch (err: any) {
          showToast(err.message || 'Error al iniciar backup', 'error')
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  const handleDescargarBackup = async (pedido: HostingPedido) => {
    setActionLoading(pedido.id)
    try {
      const { url } = await api.get<{ url: string }>(`/api/hosting/pedidos/${pedido.id}/backup-descargar`)
      window.open(url, '_blank', 'noopener')
    } catch (err: any) {
      showToast(err.message || 'Error al generar el enlace de descarga', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  const handleSyncPreview = async () => {
    setShowSyncModal(true)
    setSyncLoading(true)
    setSyncPreview(null)
    setSyncResult(null)
    try {
      const data = await api.get<WHMSyncPreview>('/api/hosting/sync/preview')
      setSyncPreview(data)
    } catch (err: any) {
      showToast(err.message || 'Error obteniendo preview', 'error')
    } finally {
      setSyncLoading(false)
    }
  }

  const handleSyncImport = async () => {
    setSyncLoading(true)
    setSyncResult(null)
    try {
      const data = await api.post<{ imported: number; skipped: number; message: string }>('/api/hosting/sync/import', {})
      setSyncResult(data.message)
      showToast(data.message, 'success')
      queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
      setSyncPreview(null)
    } catch (err: any) {
      showToast(err.message || 'Error importando', 'error')
    } finally {
      setSyncLoading(false)
    }
  }

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Hosting' }]} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter">
      <PageHeader
        breadcrumbs={[{ label: 'Hosting' }]}
        meta={`${pedidos.length} pedido${pedidos.length !== 1 ? 's' : ''}`}
        actions={
          <>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate('/hosting/nuevo')}
              title="Dar de alta un paquete de servicio gestionado (Hetzner / Supabase / Cloudflare)"
            >
              <IconPlus size={14} /> Nuevo servicio
            </button>
            <button className="btn btn-sm btn-ghost" onClick={handleSyncPreview}>
              <IconRefresh size={14} /> Sincronizar WHM
            </button>
            <button
              className={`btn btn-sm ${showServerStats ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setShowServerStats(!showServerStats)}
            >
              <IconServer size={14} /> Servidor WHM
            </button>
          </>
        }
      />

      {/* Server Stats Panel */}
      {showServerStats && (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: 12,
          padding: '16px',
          marginBottom: '16px',
        }}>
          {statsLoading ? (
            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }}>
              <div className="auth-spinner" style={{ margin: '0 auto 8px' }} />Consultando servidor...
            </div>
          ) : serverStats ? (
            <>
              <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Total cuentas</small>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{serverStats.total_accounts}</div>
                </div>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Activas</small>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--success)' }}>{serverStats.active}</div>
                </div>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Suspendidas</small>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--danger)' }}>{serverStats.suspended}</div>
                </div>
              </div>
              <details>
                <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Ver todas las cuentas ({serverStats.accounts.length})
                </summary>
                <div className="table-container" style={{ marginTop: 8 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Usuario</th><th>Dominio</th><th>Disco</th><th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serverStats.accounts.map(a => (
                        <tr key={a.user}>
                          <td><code>{a.user}</code></td>
                          <td>{a.domain}</td>
                          <td>{a.diskused} / {a.disklimit}</td>
                          <td><span className={`badge ${a.suspended ? 'badge-danger' : 'badge-success'}`}>{a.suspended ? 'Suspendida' : 'Activa'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          ) : (
            <div style={{ color: 'var(--text-secondary)' }}>Error cargando stats del servidor</div>
          )}
        </div>
      )}

      {/* Estado tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {estadoTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setFiltroEstado(tab.key)}
            className={`btn btn-sm ${filtroEstado === tab.key ? 'btn-primary' : 'btn-ghost'}`}
          >
            {tab.label}
            <span style={{ marginLeft: 6, opacity: 0.6 }}>
              {tab.key === 'todos' ? pedidos.length : pedidos.filter((p) => p.estado === tab.key).length}
            </span>
          </button>
        ))}
        <button
          onClick={() => setVerSistema(v => !v)}
          className={`btn btn-sm ${verSistema ? 'btn-primary' : 'btn-ghost'}`}
          style={{ marginLeft: 'auto' }}
          title="Cuentas de infra (reseller raíz, host de proxies): ocultas y protegidas por seguridad"
        >
          {verSistema ? '🔒 Ocultar cuentas de sistema' : '🔒 Ver cuentas de sistema'}
        </button>
      </div>

      {/* Filtro por proveedor */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <small style={{ color: 'var(--text-secondary)', marginRight: 4 }}>Proveedor:</small>
        {proveedorFiltros.map(f => {
          const count = f.key === 'todos' ? pedidos.length : pedidos.filter(p => proveedoresDePaquete(p).includes(f.key)).length
          return (
            <button
              key={f.key}
              onClick={() => setFiltroProveedor(f.key)}
              className={`btn btn-sm ${filtroProveedor === f.key ? 'btn-primary' : 'btn-ghost'}`}
            >
              {f.label}<span style={{ marginLeft: 6, opacity: 0.6 }}>{count}</span>
            </button>
          )
        })}
      </div>

      {/* Search */}
      {pedidos.length > 3 && (
        <input
          className="form-input"
          placeholder="Buscar por nombre, email o código..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: '16px', maxWidth: 400 }}
        />
      )}

      {/* Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Cliente</th>
              <th>Plan</th>
              <th>Periodo</th>
              <th>Total</th>
              <th>Estado</th>
              <th>Proveedor</th>
              <th>📎</th>
              <th>Provisioning</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                  {search || filtroEstado !== 'todos' ? 'Sin resultados' : 'Aún no hay pedidos de hosting'}
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} style={p.es_sistema ? { background: 'var(--bg-hover, rgba(120,120,140,0.06))' } : undefined}>
                <td><strong>{p.codigo}</strong>{p.es_sistema && <span title="Cuenta de sistema protegida" style={{ marginLeft: 6 }}>🔒</span>}</td>
                <td>
                  <div>{p.nombre}</div>
                  <small style={{ color: 'var(--text-secondary)' }}>{p.email}</small>
                </td>
                <td>{p.plan_nombre}</td>
                <td style={{ textTransform: 'capitalize' }}>{p.periodo}</td>
                <td>{p.moneda === 'PEN' ? 'S/' : '$'}{p.total}</td>
                <td>
                  <span className={`badge ${estadoBadge[p.estado] || 'badge-default'}`}>
                    {p.estado}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    {(() => {
                      // Badge derivado del set de componentes: varios proveedores → "Cloud (n)".
                      const provs = proveedoresDePaquete(p)
                      if (provs.length > 1) {
                        return (
                          <span
                            className="badge badge-info"
                            style={{ fontSize: '0.72rem' }}
                            title={provs.map(v => proveedorInfo[v]?.label || v).join(' + ')}
                          >
                            Cloud ({provs.length})
                          </span>
                        )
                      }
                      const pi = proveedorInfo[provs[0]] || { label: provs[0] || '—', badge: 'badge-default' }
                      return <span className={`badge ${pi.badge}`} style={{ fontSize: '0.72rem' }}>{pi.label}</span>
                    })()}
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span
                        className={`badge ${p.origen === 'sync_whm' ? 'badge-info' : 'badge-default'}`}
                        title={p.origen === 'sync_whm' ? 'Importado desde WHM' : p.origen === 'manual' ? 'Alta manual' : 'Checkout público'}
                        style={{ fontSize: '0.65rem', opacity: 0.8 }}
                      >
                        {p.origen === 'sync_whm' ? 'Sync' : p.origen === 'manual' ? 'Manual' : 'Checkout'}
                      </span>
                      {p.whm_sync_status === 'orphan' && (
                        <span className="badge badge-danger" style={{ fontSize: '0.65rem', marginLeft: 4 }} title="Cuenta eliminada del servidor">⚠️</span>
                      )}
                      {p.whm_sync_status === 'mismatch' && (
                        <span className="badge badge-warning" style={{ fontSize: '0.65rem', marginLeft: 4 }} title="Estado desincronizado">⚠️</span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  {p.comprobante_url ? (
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setComprobantePreview(p.comprobante_url!)}
                      title="Ver comprobante"
                      style={{ fontSize: '1.1rem' }}
                    >
                      📎
                    </button>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                  )}
                </td>
                <td>
                  <span className={`badge ${p.estado_provisioning === 'completado' ? 'badge-success' : p.estado_provisioning === 'en_progreso' ? 'badge-info' : 'badge-default'}`}>
                    {p.estado_provisioning || 'pendiente'}
                  </span>
                </td>
                <td>{p.created_at ? formatDate(p.created_at) : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {p.es_sistema ? (
                      <span
                        className="badge badge-default"
                        title="Cuenta de infra (reseller raíz / host de proxies). Protegida: no se puede suspender ni eliminar."
                        style={{ fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        🔒 Protegida
                      </span>
                    ) : (
                    <>
                    {p.mp_payment_id && (
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => openReembolso(p)}
                        title="Reembolsar (parcial) en MercadoPago"
                        style={{ color: 'var(--danger)' }}
                      >
                        Reembolsar
                      </button>
                    )}
                    {p.estado === 'pendiente' && p.metodo_pago === 'transferencia' && (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleConfirmarPago(p)}
                        title="Confirmar pago"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                    )}
                    {p.estado === 'pendiente' && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleCancelar(p)}
                        disabled={actionLoading === p.id}
                        title="Rechazar / Cancelar"
                      >
                        {actionLoading === p.id ? '...' : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        )}
                      </button>
                    )}
                    {p.estado === 'pagado' && p.estado_provisioning !== 'completado' && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => { setSelectedPedido(p); setShowProvisionModal(true) }}
                      >
                        Provisionar
                      </button>
                    )}
                    {p.estado === 'activo' && (
                      <>
                        <button className="btn btn-sm btn-ghost" onClick={() => setSelectedPedido(p)}>
                          Ver
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => p.backup_status === 'ready' ? handleDescargarBackup(p) : handleBackup(p)}
                          disabled={actionLoading === p.id || (p.backup_status === 'generating' || p.backup_status === 'generating_delete')}
                          title={(p.backup_status === 'generating' || p.backup_status === 'generating_delete') ? 'Backup en progreso...' : p.backup_status === 'ready' ? 'Descargar copia de seguridad (interno, sin avisar al cliente)' : 'Generar copia de seguridad interna'}
                          style={(p.backup_status === 'generating' || p.backup_status === 'generating_delete') ? { opacity: 0.5 } : undefined}
                        >
                          {(p.backup_status === 'generating' || p.backup_status === 'generating_delete') ? 'Generando...' : p.backup_status === 'ready' ? 'Descargar ✓' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleSuspender(p)}
                          disabled={actionLoading === p.id}
                          title="Suspender cuenta"
                        >
                          {actionLoading === p.id ? '...' : 'Suspender'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleTerminar(p)}
                          disabled={actionLoading === p.id}
                          title="Dar de baja: genera backup y luego elimina"
                          style={{ background: 'color-mix(in srgb, var(--danger) 80%, black)' }}
                        >
                          {actionLoading === p.id ? '...' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          )}
                        </button>
                      </>
                    )}
                    {p.estado === 'suspendido' && (
                      <>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => p.backup_status === 'ready' ? handleDescargarBackup(p) : handleBackup(p)}
                          disabled={actionLoading === p.id || (p.backup_status === 'generating' || p.backup_status === 'generating_delete')}
                          title={(p.backup_status === 'generating' || p.backup_status === 'generating_delete') ? 'Backup en progreso...' : p.backup_status === 'ready' ? 'Descargar copia de seguridad (interno, sin avisar al cliente)' : 'Generar copia de seguridad interna'}
                          style={(p.backup_status === 'generating' || p.backup_status === 'generating_delete') ? { opacity: 0.5 } : undefined}
                        >
                          {(p.backup_status === 'generating' || p.backup_status === 'generating_delete') ? 'Generando...' : p.backup_status === 'ready' ? 'Descargar ✓' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                          )}
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleReactivar(p)}
                          disabled={actionLoading === p.id}
                        >
                          {actionLoading === p.id ? '...' : 'Reactivar'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleTerminar(p)}
                          disabled={actionLoading === p.id}
                          title="Dar de baja: genera backup y luego elimina"
                          style={{ background: 'color-mix(in srgb, var(--danger) 80%, black)' }}
                        >
                          {actionLoading === p.id ? '...' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          )}
                        </button>
                      </>
                    )}
                    {p.estado === 'cancelado' && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleEliminar(p)}
                        disabled={actionLoading === p.id}
                        title="Eliminar permanentemente"
                      >
                        {actionLoading === p.id ? '...' : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        )}
                      </button>
                    )}
                    </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer for selected pedido */}
      {selectedPedido && !showProvisionModal && (
        <div className="modal-overlay" onClick={() => setSelectedPedido(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Pedido {selectedPedido.codigo}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedPedido(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Plan</small>
                  <div>{selectedPedido.plan_nombre} — {selectedPedido.periodo}</div>
                </div>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Total</small>
                  <div>{selectedPedido.moneda === 'PEN' ? 'S/' : '$'}{selectedPedido.total}</div>
                </div>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Cliente</small>
                  <input
                    className="form-input"
                    value={contactForm.nombre}
                    onChange={e => setContactForm(f => ({ ...f, nombre: e.target.value }))}
                    placeholder="Nombre del cliente"
                  />
                </div>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Email</small>
                  <input
                    className="form-input"
                    type="email"
                    value={contactForm.email}
                    onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>WhatsApp</small>
                  <input
                    className="form-input"
                    value={contactForm.whatsapp}
                    onChange={e => setContactForm(f => ({ ...f, whatsapp: e.target.value }))}
                    placeholder="51999999999"
                  />
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleGuardarContacto}
                    disabled={savingContact || !contactoCambio}
                    title={contactoCambio ? 'Guardar cambios de contacto' : 'Sin cambios'}
                  >
                    {savingContact ? 'Guardando...' : 'Guardar datos de contacto'}
                  </button>
                </div>
                {selectedPedido.dominio && (
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>Dominio</small>
                    <div>{selectedPedido.dominio}</div>
                  </div>
                )}
                {selectedPedido.cpanel_url && (
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>cPanel URL</small>
                    <div><a href={selectedPedido.cpanel_url} target="_blank" rel="noreferrer">{selectedPedido.cpanel_url}</a></div>
                  </div>
                )}
                {selectedPedido.cpanel_usuario && (
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>Usuario cPanel</small>
                    <div><code>{selectedPedido.cpanel_usuario}</code></div>
                  </div>
                )}
                {selectedPedido.ip_servidor && (
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>IP Servidor</small>
                    <div><code>{selectedPedido.ip_servidor}</code></div>
                  </div>
                )}
                <div>
                  <small style={{ color: 'var(--text-secondary)' }}>Paquete</small>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="badge badge-default" style={{ textTransform: 'capitalize' }}>
                      {selectedPedido.tipo_paquete || 'hosting'}
                    </span>
                    {proveedoresDePaquete(selectedPedido).map(v => {
                      const pi = proveedorInfo[v] || { label: v, badge: 'badge-info' }
                      return <span key={v} className={`badge ${pi.badge}`}>{pi.label}</span>
                    })}
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      Corte: {selectedPedido.suspension_modo === 'manual' ? 'manual' : 'automático'}
                    </span>
                  </div>
                </div>
                {Array.isArray(selectedPedido.componentes) && selectedPedido.componentes.length > 0 && (
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>Componentes</small>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedPedido.componentes.map((comp, i) => (
                        <code key={i} style={{ fontSize: '0.78rem' }}>{comp.tipo}: {comp.recurso_ref}</code>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>Vencimiento</small>
                    <input
                      className="form-input"
                      type="date"
                      value={fechasForm.fecha_vencimiento}
                      onChange={e => setFechasForm(f => ({ ...f, fecha_vencimiento: e.target.value }))}
                    />
                  </div>
                  <div>
                    <small style={{ color: 'var(--text-secondary)' }}>Fecha de corte</small>
                    <input
                      className="form-input"
                      type="date"
                      value={fechasForm.fecha_corte}
                      onChange={e => setFechasForm(f => ({ ...f, fecha_corte: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleGuardarFechas}
                    disabled={savingFechas || !fechasCambio}
                    title={fechasCambio ? 'Guardar fechas' : 'Sin cambios'}
                  >
                    {savingFechas ? 'Guardando...' : 'Guardar fechas'}
                  </button>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    La fecha de corte es cuándo se auto-suspende. Puedes posponerla para darle más días al cliente.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provision Modal — Auto or Manual */}
      {showProvisionModal && selectedPedido && (
        <div className="modal-overlay" onClick={() => setShowProvisionModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>Provisionar — {selectedPedido.plan_nombre}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowProvisionModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 16, color: 'var(--text-secondary)' }}>
                Cliente: <strong>{selectedPedido.nombre}</strong> — {selectedPedido.email}
              </p>

              {/* Mode selector */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button
                  className={`btn btn-sm ${provisionMode === 'auto' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setProvisionMode('auto')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                    <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                  </svg>
                  Automático (WHM)
                </button>
                <button
                  className={`btn btn-sm ${provisionMode === 'manual' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setProvisionMode('manual')}
                >
                  Manual
                </button>
              </div>

              {/* Domain field — optional, auto-generates temp if empty */}
              <div className="form-group">
                <label className="form-label">Dominio <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>(opcional)</span></label>
                <input
                  className="form-input"
                  value={provisionForm.dominio}
                  onChange={e => setProvisionForm(f => ({ ...f, dominio: e.target.value }))}
                  placeholder="ejemplo.com"
                />
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Si el cliente no tiene dominio, déjalo vacío. Se asignará un subdominio temporal automáticamente.
                </p>
              </div>

              {provisionMode === 'auto' && (
                <div style={{
                  background: 'color-mix(in srgb, var(--success) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--success) 20%, transparent)',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)',
                }}>
                  <strong style={{ color: 'var(--success)' }}>Modo automático:</strong> Se creará la cuenta cPanel automáticamente en WHM. El usuario y contraseña se generan y envían al cliente por email.
                </div>
              )}

              {provisionMode === 'manual' && (
                <>
                  <div className="form-group">
                    <label className="form-label">URL cPanel</label>
                    <input
                      className="form-input"
                      value={provisionForm.cpanel_url}
                      onChange={e => setProvisionForm(f => ({ ...f, cpanel_url: e.target.value }))}
                      placeholder="https://srv1.1conecta.com:2083"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Usuario cPanel</label>
                    <input
                      className="form-input"
                      value={provisionForm.cpanel_usuario}
                      onChange={e => setProvisionForm(f => ({ ...f, cpanel_usuario: e.target.value }))}
                      placeholder="usuario"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contraseña cPanel</label>
                    <input
                      className="form-input"
                      type="password"
                      value={provisionForm.cpanel_password}
                      onChange={e => setProvisionForm(f => ({ ...f, cpanel_password: e.target.value }))}
                      placeholder="Contraseña"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">IP del Servidor</label>
                    <input
                      className="form-input"
                      value={provisionForm.ip_servidor}
                      onChange={e => setProvisionForm(f => ({ ...f, ip_servidor: e.target.value }))}
                      placeholder="190.92.174.30"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowProvisionModal(false)}>Cancelar</button>
              <button
                className="btn btn-primary"
                onClick={handleProvisionar}
                disabled={provisionLoading || (provisionMode === 'manual' && (!provisionForm.cpanel_url || !provisionForm.cpanel_usuario || !provisionForm.cpanel_password))}
              >
                {provisionLoading ? 'Provisionando...' : provisionMode === 'auto' ? 'Crear cuenta automáticamente' : 'Provisionar y notificar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync WHM Modal */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => { setShowSyncModal(false); setSyncPreview(null); setSyncResult(null) }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 650, width: '90vw' }}>
            <div className="modal-header">
              <h3>Sincronizar con WHM</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowSyncModal(false); setSyncPreview(null); setSyncResult(null) }}>✕</button>
            </div>
            <div className="modal-body">
              {syncLoading && !syncPreview && !syncResult && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
                  Consultando servidor WHM...
                </div>
              )}

              {syncResult && (
                <div style={{
                  background: 'color-mix(in srgb, var(--success) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--success) 20%, transparent)',
                  borderRadius: 8,
                  padding: 16,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--success)', marginBottom: 8 }}>Sincronización completada</div>
                  <p style={{ color: 'var(--text-secondary)' }}>{syncResult}</p>
                </div>
              )}

              {syncPreview && !syncResult && (
                <>
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, flex: 1, minWidth: 120 }}>
                      <small style={{ color: 'var(--text-secondary)' }}>Cuentas en WHM</small>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{syncPreview.in_whm}</div>
                    </div>
                    <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 12, flex: 1, minWidth: 120 }}>
                      <small style={{ color: 'var(--text-secondary)' }}>En ERP</small>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{syncPreview.in_erp}</div>
                    </div>
                    <div style={{ background: 'color-mix(in srgb, var(--primary-light) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--brand-500) 15%, transparent)', borderRadius: 8, padding: 12, flex: 1, minWidth: 120 }}>
                      <small style={{ color: 'var(--text-secondary)' }}>Por importar</small>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--brand-500)' }}>{syncPreview.to_import.length}</div>
                    </div>
                  </div>

                  {syncPreview.to_import.length > 0 && (
                    <>
                      <p style={{ fontWeight: 600, marginBottom: 8, color: 'var(--brand-500)' }}>
                        Cuentas para importar ({syncPreview.to_import.length})
                      </p>
                      <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 16 }}>
                        <table style={{ width: '100%', fontSize: '0.82rem' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Dominio</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Usuario</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Plan WHM</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Plan ERP</th>
                              <th style={{ textAlign: 'center', padding: '6px 8px' }}>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {syncPreview.to_import.map((acct, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={{ padding: '6px 8px' }}>{acct.domain}</td>
                                <td style={{ padding: '6px 8px' }}><code>{acct.user}</code></td>
                                <td style={{ padding: '6px 8px', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{acct.plan}</td>
                                <td style={{ padding: '6px 8px' }}>
                                  {acct.plan_mapped ? (
                                    <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>{acct.plan_mapped}</span>
                                  ) : (
                                    <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Desconocido</span>
                                  )}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                  <span className={`badge ${acct.suspended ? 'badge-danger' : 'badge-success'}`} style={{ fontSize: '0.7rem' }}>
                                    {acct.suspended ? 'Suspendida' : 'Activa'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {syncPreview.to_import.some(a => !a.plan_mapped) && (
                        <p style={{ fontSize: '0.78rem', color: 'var(--warning)', marginBottom: 12 }}>
                          ⚠️ Algunos planes no pudieron mapearse a los planes del ERP. Se importarán con el nombre original del package WHM.
                        </p>
                      )}
                    </>
                  )}

                  {syncPreview.to_update.length > 0 && (
                    <div style={{
                      background: 'color-mix(in srgb, var(--warning) 6%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--warning) 20%, transparent)',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                    }}>
                      <p style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>
                        Estados desincronizados ({syncPreview.to_update.length})
                      </p>
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        Estas cuentas tienen un estado diferente en WHM. Al importar se actualizarán automáticamente.
                      </p>
                    </div>
                  )}

                  {syncPreview.to_import.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 16, color: 'var(--success)' }}>
                      Todas las cuentas de WHM ya están registradas en el ERP.
                    </div>
                  )}
                </>
              )}
            </div>
            {syncPreview && syncPreview.to_import.length > 0 && !syncResult && (
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => { setShowSyncModal(false); setSyncPreview(null) }}>Cancelar</button>
                <button
                  className="btn btn-primary"
                  onClick={handleSyncImport}
                  disabled={syncLoading}
                >
                  {syncLoading ? 'Importando...' : `Importar ${syncPreview.to_import.length} cuenta${syncPreview.to_import.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comprobante Preview Modal */}
      {comprobantePreview && (
        <div className="modal-overlay" onClick={() => setComprobantePreview(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, width: '90vw' }}>
            <div className="modal-header">
              <h3>Comprobante de pago</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <a
                  href={comprobantePreview}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-ghost"
                  title="Abrir en nueva pestaña"
                >
                  ↗
                </a>
                <button className="btn btn-ghost btn-sm" onClick={() => setComprobantePreview(null)}>✕</button>
              </div>
            </div>
            {/* Fondo oscuro fijo, como el de cualquier visor de PDF: el
                comprobante es una hoja blanca y necesita contraste alrededor
                en los dos temas. */}
            <div className="modal-body" style={{ padding: 0, textAlign: 'center', background: '#111' }}>
              {comprobantePreview.match(/\.pdf$/i) ? (
                <iframe
                  src={comprobantePreview}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                  title="Comprobante PDF"
                />
              ) : (
                <img
                  src={comprobantePreview}
                  alt="Comprobante"
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reembolso Modal — mueve dinero real, el admin confirma el monto */}
      {reembolsoPedido && (
        <div className="modal-overlay" onClick={() => !reembolsoLoading && setReembolsoPedido(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3>Reembolsar — {reembolsoPedido.codigo}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setReembolsoPedido(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>
                Cliente: <strong>{reembolsoPedido.nombre}</strong><br />
                Pagó <strong>{reembolsoPedido.moneda === 'PEN' ? 'S/' : '$'}{reembolsoPedido.total}</strong> por MercadoPago.
              </p>
              <label className="form-label">Monto a reembolsar ({reembolsoPedido.moneda})</label>
              <input
                className="form-input"
                type="number"
                step="0.01"
                value={reembolsoMonto}
                onChange={e => setReembolsoMonto(e.target.value)}
                placeholder="0.00"
                autoFocus
              />
              {reembolsoPedido.dominio_cobro != null && (
                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  Prellenado con lo cobrado por el dominio (S/{reembolsoPedido.dominio_cobro}). Puedes editarlo.
                </span>
              )}
              <div style={{ background: 'color-mix(in srgb, var(--danger) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)', borderRadius: 8, padding: '10px 12px', marginTop: 14, fontSize: '0.78rem', color: 'var(--danger)' }}>
                ⚠️ Esto ejecuta un reembolso REAL en MercadoPago. Verifica el monto antes de continuar.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setReembolsoPedido(null)} disabled={reembolsoLoading}>Cancelar</button>
              <button className="btn btn-danger" onClick={doReembolso} disabled={reembolsoLoading}>
                {reembolsoLoading ? 'Procesando…' : 'Reembolsar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checklist de pasos manuales (Supabase, etc.) devuelto por suspender/reactivar */}
      {checklistModal && (
        <div className="modal-overlay" onClick={() => setChecklistModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3>{checklistModal.titulo}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setChecklistModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
                Para completar la operación, realiza estos pasos manualmente:
              </p>
              <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {checklistModal.items.map((item, i) => (
                  <li key={i} style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>{item}</li>
                ))}
              </ol>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setChecklistModal(null)}>Entendido</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal — reemplaza browser confirm() */}
      {confirmModal && (
        <div className="modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>{confirmModal.title}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{confirmModal.message}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setConfirmModal(null)}>Cancelar</button>
              <button
                className={`btn btn-${confirmModal.variant}`}
                onClick={confirmModal.onConfirm}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
